import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { MEMBERSHIP_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/membershipsColumnsConfig';
import { buildMembershipsFilterGroups } from '../constants/membershipsFiltersConfig';
import { currencyFormatter } from '../utils/reportingUtils';

const axios = require('axios').default;

const STATUS_BADGES = {
  active: { bg: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)' },
  trialing: { bg: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)' },
  past_due: { bg: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)' },
  canceled: { bg: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)' },
  paused: { bg: 'var(--sl-bg-warning-primary, #fffaeb)', color: 'var(--sl-text-warning, #b54708)' },
  incomplete: { bg: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)' },
  incomplete_expired: { bg: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)' },
  unpaid: { bg: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)' },
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  if (activeFilters.membership_product && activeFilters.membership_product.length > 0) {
    params.membership_product = activeFilters.membership_product;
  }
  if (activeFilters.status && activeFilters.status.length > 0) {
    params.status = activeFilters.status;
  }
  if (activeFilters.interval && activeFilters.interval.length > 0) {
    params.interval = activeFilters.interval;
  }
  if (activeFilters.sold_by_team_membership_id && activeFilters.sold_by_team_membership_id.length > 0) {
    params.sold_by_team_membership_id = activeFilters.sold_by_team_membership_id;
  }
  return params;
};

const MEMBERSHIPS_CONFIG = {
  pageTitle: 'Memberships',
  pageSubtitle: 'Membership subscriptions across your venues.',
  sidebarKey: 'sales_memberships',
  defaultPreset: 'year_to_date',
  urlFilterIds: ['venue_ownership_id'],
  dateModeOptions: [{ key: 'created_date', label: 'Joined Date' }],
  defaultSortRules: [{ field: 'created_at', direction: 'desc' }],

  columns: MEMBERSHIP_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingSalesMemberships_${teamId}`,
  firstColumnId: 'member_name',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/dashboard/sales/memberships/data.json`,
  detailResponseKey: 'memberships',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/dashboard/sales/memberships/export`,
  detailErrorMessage: 'Failed to load membership data.',
  detailTableTitle: 'Memberships',
  emptyMessage: 'No memberships found.',
  searchPlaceholder: 'Search memberships...',
  paginationLabel: 'memberships',
  itemLabel: (count) => `membership${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current) => [
    { label: 'Total Active Members', value: (current.total_active || 0).toLocaleString() },
    { label: 'New This Period', value: (current.new_this_period || 0).toLocaleString() },
    { label: 'Churned This Period', value: (current.churned_this_period || 0).toLocaleString() },
    { label: 'MRR', value: currencyFormatter.format(current.mrr || 0) },
  ],
  kpiGridColumns: 4,

  chartColor: '#7c3aed',
  overTimeKey: null,
  salesCardTitle: null,

  topCharts: [],

  groupByOptions: null,
  groupByDefault: null,

  renderCellValue: (col, membership, { formatEventDate, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'member_name': return membership.member_name || '\u2014';
      case 'email': return membership.email || '\u2014';
      case 'product_name': return membership.product_name || '\u2014';
      case 'status': {
        const badge = STATUS_BADGES[membership.status] || STATUS_BADGES.incomplete;
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 16,
            fontSize: 12,
            fontWeight: 500,
            background: badge.bg,
            color: badge.color,
          }}>
            {membership.status ? membership.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '\u2014'}
          </span>
        );
      }
      case 'amount': return cf.format(membership.amount || 0);
      case 'interval': return membership.interval ? membership.interval.charAt(0).toUpperCase() + membership.interval.slice(1) : '\u2014';
      case 'current_period_start': return <span className="text-nowrap">{membership.current_period_start ? formatEventDate(membership.current_period_start) : '\u2014'}</span>;
      case 'current_period_end': return <span className="text-nowrap">{membership.current_period_end ? formatEventDate(membership.current_period_end) : '\u2014'}</span>;
      case 'created_at': return <span className="text-nowrap">{membership.created_at ? formatEventDate(membership.created_at) : '\u2014'}</span>;
      case 'venue_name': return membership.venue_name || '\u2014';
      case 'canceled_at': return <span className="text-nowrap">{membership.canceled_at ? formatEventDate(membership.canceled_at) : '\u2014'}</span>;
      case 'net_to_venue': return cf.format(membership.net_to_venue || 0);
      case 'processing_fee': return cf.format(membership.processing_fee || 0);
      case 'sold_by': return membership.sold_by || '\u2014';
      case 'device_name': return membership.device_name || '\u2014';
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Memberships',
  filterSubtitle: 'Apply filters to narrow membership data.',
};

function ReportingSalesMembershipsPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/dashboard/sales/memberships/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team && team.id, csrfToken]);

  const configWithFilters = {
    ...MEMBERSHIPS_CONFIG,
    buildFilterGroups: (venueOwnerships) => buildMembershipsFilterGroups(venueOwnerships, filterOptions),
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingSalesMembershipsPage;
